/**
 * Google Analytics 4 + Consent Mode v2
 *
 * GA4 loads with analytics_storage denied by default (set in index.html).
 * When the user accepts cookies, grantAnalyticsConsent() updates the consent
 * and GA4 begins storing analytics cookies. If the user chooses "Necessary Only",
 * GA4 still runs in cookieless mode (no PII, no persistent identifiers).
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/** Grant analytics cookie consent (called when user clicks "Accept All") */
export function grantAnalyticsConsent() {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  }
}

/** Check if user has accepted analytics cookies */
export function hasAnalyticsConsent(): boolean {
  return localStorage.getItem('cookie_consent') === 'accepted';
}

/** Track a page view (called automatically on route change) */
export function trackPageView(path: string, title?: string) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  }
}

/** Track a custom event */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

// Pre-defined event helpers for common actions
export const Analytics = {
  /** User starts the life purpose questionnaire */
  questStart: () => trackEvent('quest_start', { category: 'life_purpose' }),

  /** User completes the questionnaire */
  questComplete: (formResult: string) =>
    trackEvent('quest_complete', { category: 'life_purpose', form_result: formResult }),

  /** User initiates payment */
  paymentStart: (provider: string, amount: number) =>
    trackEvent('begin_checkout', { currency: provider === 'stripe' ? 'GBP' : 'INR', value: amount }),

  /** Payment successful */
  paymentSuccess: (provider: string, amount: number) =>
    trackEvent('purchase', { currency: provider === 'stripe' ? 'GBP' : 'INR', value: amount }),

  /** User reads a form detail page */
  viewForm: (formSlug: string) =>
    trackEvent('view_form', { form_slug: formSlug }),

  /** User reads a stotra */
  viewStotra: (stotraSlug: string) =>
    trackEvent('view_stotra', { stotra_slug: stotraSlug }),

  /** User reads an article */
  viewArticle: (articleSlug: string) =>
    trackEvent('view_article', { article_slug: articleSlug }),

  /** User starts a japa session */
  japaStart: (formSlug: string) =>
    trackEvent('japa_start', { form_slug: formSlug }),

  /** Newsletter signup */
  newsletterSignup: () => trackEvent('newsletter_signup'),

  /** Contact form submission */
  contactSubmit: () => trackEvent('contact_submit'),

  /** User plays a documentary/film */
  filmPlay: (filmSlug: string) =>
    trackEvent('film_play', { film_slug: filmSlug }),

  /** User starts the Bhairava personality quiz */
  quizStart: () => trackEvent('quiz_start', { category: 'bhairava_quiz' }),

  /** User completes the quiz */
  quizComplete: (formSlug: string) =>
    trackEvent('quiz_complete', { category: 'bhairava_quiz', form_slug: formSlug }),

  /** User shares their quiz result */
  quizShare: (platform: string, formSlug: string) =>
    trackEvent('quiz_share', { platform, form_slug: formSlug }),

  /** User clicks the Life Purpose upsell from quiz result */
  quizUpsellClick: (formSlug: string) =>
    trackEvent('quiz_upsell_click', { category: 'bhairava_quiz', form_slug: formSlug }),

  /** The post-japa push-notification prompt was shown */
  pushPromptShown: () => trackEvent('push_prompt_shown', { category: 'notifications' }),

  /** User granted permission and subscribed to push notifications */
  pushSubscribed: (language: string) =>
    trackEvent('push_subscribed', { category: 'notifications', language }),

  /** User declined or was blocked from subscribing (reason: permission | error) */
  pushDenied: (reason: string) =>
    trackEvent('push_denied', { category: 'notifications', reason }),

  /** User arrived via a notification or email link (source: push | email) */
  notifReturnVisit: (source: string) =>
    trackEvent('notif_return_visit', { category: 'notifications', source }),

  /** User presses play on the Life Purpose demo video (page: home | forms | life_purpose_landing) */
  demoVideoPlay: (page: string) =>
    trackEvent('demo_video_play', { category: 'life_purpose', page }),

  /** User's watch position crosses a quarter of the demo video (percent: 25 | 50 | 75 | 100) */
  demoVideoProgress: (page: string, percent: number) =>
    trackEvent('demo_video_progress', { category: 'life_purpose', page, percent }),

  /** User pressed Submit on the last questionnaire step (signed_in: whether they were logged in) */
  questSubmit: (signedIn: boolean) =>
    trackEvent('quest_submit', { category: 'life_purpose', signed_in: signedIn }),

  /** A logged-out visitor was sent to login at questionnaire submit */
  questLoginRedirect: () => trackEvent('quest_login_redirect', { category: 'life_purpose' }),

  /** The questionnaire was saved on the server (fires for every saved submission) */
  questSaved: () => trackEvent('quest_saved', { category: 'life_purpose' }),

  /** The questionnaire could not be saved (reason: age | server | network) */
  questSubmitError: (reason: string) =>
    trackEvent('quest_submit_error', { category: 'life_purpose', reason }),

  /** The payment page was shown to someone who can pay */
  paymentView: (currency: string) =>
    trackEvent('payment_view', { category: 'life_purpose', currency }),

  /** User picked a reading on the payment page (tier: regular | premium) */
  paymentTierSelect: (tier: string) =>
    trackEvent('payment_tier_select', { category: 'life_purpose', tier }),

  /** User saved the situation text a personal reading needs */
  premiumSummarySaved: () => trackEvent('premium_summary_saved', { category: 'life_purpose' }),

  /** A logged-out visitor was sent to login from the payment page */
  paymentLoginRedirect: () => trackEvent('payment_login_redirect', { category: 'life_purpose' }),
};
